/**
 * Context Panel (Left Sidebar)
 *
 * Workflow-aware accordion panel. The sidebar's sections are derived from
 * the active workflow's compiled manifest:
 *   - One section per `source: 'context'` input declared in the workflow
 *   - `type: 'sheet-row'`     → rendered by <SheetRowSection> (tab + row picker)
 *   - `type: 'product-list'`  → rendered by the existing Products section
 *
 * Sections are sorted alphabetically by their display title so the layout is
 * predictable independent of the order nodes appear in the Comfy graph.
 *
 * Selections are written to StudioContext (`sheetSelections` / `selectedProducts`)
 * and injected into the job payload by WorkflowStudio at submit time.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useStudioContext, Product, SheetSelection } from '../../contexts/StudioContext';
import { productService } from '../../services/productService';
import { WorkflowDetails, ManifestInput } from '../../types/workflow';
import { getApiBaseUrl } from '../../config/environment';

const API_BASE = getApiBaseUrl();

/** Route any URL through the server-side image proxy to avoid Google Drive auth restrictions. */
const proxyImageUrl = (url: string) =>
  `${API_BASE}/api/image-proxy?url=${encodeURIComponent(url)}`;

interface ContextPanelProps {
  accessToken: string;
  tenantId: string;
  selectedWorkflow: WorkflowDetails | null;
}

// ─── Icon helpers ──────────────────────────────────────────────────────────────

const IconChevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150 text-gray-500 ${open ? 'rotate-90' : ''}`}
    fill="none" stroke="currentColor" viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const IconBox: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

/** Generic "sheet/list" icon used for any sheet-driven context section. */
const IconSheet: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

// ─── Reusable section header ───────────────────────────────────────────────────

interface SectionHeaderProps {
  icon: React.ReactNode;
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  /** Content placed on the right before the clear button */
  badge?: React.ReactNode;
  /** When provided, renders an × button that fires this handler */
  onClear?: (e: React.MouseEvent) => void;
  /** When true, renders a small red dot next to the label to flag an unmet
   *  required selection. Workflow-declared context sections are required by
   *  default; the dot should clear as soon as a selection is made. */
  required?: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon, label, isOpen, onToggle, badge, onClear, required,
}) => (
  // Outer row is a plain flex container — no interactive role — so the clear
  // <button> inside is a valid, non-nested interactive element.
  <div className="flex items-center hover:bg-white/5 transition-colors duration-fast group">
    {/* Toggle area — takes all available space */}
    <button
      type="button"
      onClick={onToggle}
      className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left min-w-0 touch-target"
    >
      <IconChevron open={isOpen} />
      <span className="text-gray-500 group-hover:text-gray-400 transition-colors flex-shrink-0">
        {icon}
      </span>
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-gray-400 transition-colors">
        {label}
      </span>
      {required && (
        <span
          className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"
          title="Required selection"
          aria-label="Required"
        />
      )}
      {badge && (
        <div className="ml-auto flex items-center gap-1.5 min-w-0 mr-1">
          {badge}
        </div>
      )}
    </button>
    {/* Clear button — sibling of the toggle button, not a child */}
    {onClear && (
      <button
        type="button"
        onClick={onClear}
        className="flex-shrink-0 touch-target mr-2 flex items-center justify-center rounded text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-fast"
        title="Clear selection"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    )}
  </div>
);

// ─── SheetRowSection ───────────────────────────────────────────────────────────

/** Wire payload from GET /api/sheet-data. */
interface SheetDataResponse {
  sheetId: string;
  tabs: string[];
  rowsByTab: Record<string, Record<string, string>[]>;
  fetchedAt: number;
  cached: boolean;
}

interface SheetRowSectionProps {
  input: ManifestInput;
  accessToken: string;
}

/**
 * Renders one sidebar section backed by a CCStudio_SheetSelector node.
 *
 * Fetches the sheet on mount, lets the user pick a tab (unless the workflow
 * pinned one via `locked_tab`) and a row, and writes the selection back to
 * StudioContext so WorkflowStudio can inject `${contextId}__tab` and
 * `${contextId}__row` at submit time.
 */
const SheetRowSection: React.FC<SheetRowSectionProps> = ({ input, accessToken }) => {
  const { context, setSheetSelection } = useStudioContext();
  const contextId = input.context_id || input.key;
  const sheetId   = input.sheet_id || '';
  const lockedTab = input.locked_tab;
  const sectionLabel = input.title || contextId;

  const [data, setData]       = useState<SheetDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [tab, setTab]         = useState<string>(lockedTab || '');
  const [search, setSearch]   = useState('');
  const [isOpen, setIsOpen]   = useState(true);

  // Fetch sheet data when sheetId changes. Token changes don't refetch.
  useEffect(() => {
    if (!sheetId) {
      setError('Missing sheet_id on workflow node');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/sheet-data?sheetId=${encodeURIComponent(sheetId)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<SheetDataResponse>;
      })
      .then(d => {
        if (cancelled) return;
        setData(d);
        const existing = context.sheetSelections[contextId]?.tab;
        const desired =
          lockedTab && d.tabs.includes(lockedTab) ? lockedTab
          : existing && d.tabs.includes(existing) ? existing
          : d.tabs[0] || '';
        setTab(desired);
      })
      .catch(err => { if (!cancelled) setError(String(err.message || err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sheetId, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.rowsByTab[tab] || [];
  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.Title || '').toLowerCase().includes(q)
        || (r.Description || '').toLowerCase().includes(q);
  });

  const selection = context.sheetSelections[contextId];

  const handleSelect = (row: Record<string, string>) => {
    if (!data) return;
    const rowTitle = row.Title || '';
    if (!rowTitle) return;
    if (selection?.rowTitle === rowTitle && selection?.tab === tab) {
      setSheetSelection(contextId, null);
    } else {
      const next: SheetSelection = { contextId, sheetId: data.sheetId, tab, rowTitle, row };
      setSheetSelection(contextId, next);
    }
  };

  // Hide the section entirely once data has loaded if no rows are selectable.
  // For a locked-tab section that's the locked tab; otherwise it's any tab.
  // Loading and error states still render so the user gets feedback — only the
  // empty post-load case is silently dropped.
  if (data && !error && !loading) {
    const tabsToConsider = lockedTab ? [lockedTab] : data.tabs;
    const anyRows = tabsToConsider.some(t => (data.rowsByTab[t] || []).length > 0);
    if (!anyRows) return null;
  }

  return (
    <div className="border-b border-gray-700 flex-shrink-0">
      <SectionHeader
        icon={<IconSheet />}
        label={sectionLabel}
        isOpen={isOpen}
        onToggle={() => setIsOpen(o => !o)}
        required={!selection}
        badge={selection && (
          <span className="text-[10px] font-medium text-purple-300 bg-purple-400/15 rounded px-1.5 py-0.5 truncate max-w-[110px]">
            {selection.rowTitle}
          </span>
        )}
        onClear={selection ? (e) => { e.stopPropagation(); setSheetSelection(contextId, null); } : undefined}
      />

      {isOpen && (
        <div className="px-2 pb-2 space-y-2">
          {!lockedTab && data && data.tabs.length > 1 && (
            <select
              value={tab}
              onChange={(e) => setTab(e.target.value)}
              aria-label={`${sectionLabel} tab`}
              title={`${sectionLabel} tab`}
              className="w-full bg-white/8 border border-white/10 rounded-md px-2 py-1 text-label text-dark-text outline-none focus:border-purple-400 transition-colors duration-fast"
            >
              {data.tabs.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}

          {data && rows.length > 5 && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${sectionLabel.toLowerCase()}…`}
              className="w-full bg-white/8 border border-white/10 rounded-md px-2.5 py-1.5 text-label text-dark-text placeholder:text-dark-muted outline-none focus:border-purple-400 transition-colors duration-fast"
            />
          )}

          <div className="max-h-72 overflow-y-auto space-y-1 scrollbar-touch">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-400" />
              </div>
            ) : error ? (
              <div className="text-center py-4 text-red-400 text-xs px-2">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-4 text-gray-600 text-xs px-2">
                {search ? 'No matches' : 'No rows in this tab'}
              </div>
            ) : filtered.map((row, idx) => {
              const rowTitle = row.Title || `Row ${idx + 1}`;
              const isSelected = selection?.rowTitle === rowTitle && selection?.tab === tab;
              const thumb = row.Thumbnail || row.thumbnail;
              const desc  = row.Description || row.description || '';
              return (
                <button
                  type="button"
                  key={`${tab}-${rowTitle}-${idx}`}
                  onClick={() => handleSelect(row)}
                  className={`w-full flex items-start gap-2 p-2.5 rounded-lg text-left transition-all duration-base touch-target ${
                    isSelected
                      ? 'bg-purple-500/15 border border-purple-400/40'
                      : 'bg-white/5 border border-transparent hover:bg-white/8 hover:border-white/15'
                  }`}
                >
                  {thumb ? (
                    <img
                      src={proxyImageUrl(thumb)}
                      alt={rowTitle}
                      className="w-10 h-10 rounded object-cover flex-shrink-0 bg-gray-700"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-purple-400 bg-purple-400' : 'border-gray-600'
                    }`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-label font-medium text-dark-text truncate">{rowTitle}</div>
                    {desc && <div className="text-meta text-dark-muted line-clamp-2">{desc}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── ProductsSection ───────────────────────────────────────────────────────────

interface ProductsSectionProps {
  input: ManifestInput;
  products: Product[];
  loading: boolean;
  search: string;
  onSearchChange: (q: string) => void;
  productsOpen: boolean;
  onToggleOpen: () => void;
  isProductSelected: (id: string) => boolean;
  onToggleProduct: (p: Product) => void;
  onClear: () => void;
  selectedCount: number;
}

/**
 * Renders the Products picker for any workflow that declares a `product-list`
 * context input. Mirrors the legacy hardcoded section, but is now driven by
 * the manifest so the section title and behaviour are workflow-controlled.
 */
const ProductsSection: React.FC<ProductsSectionProps> = ({
  input, products, loading, search, onSearchChange,
  productsOpen, onToggleOpen, isProductSelected, onToggleProduct,
  onClear, selectedCount,
}) => {
  const label = input.title || 'Products';
  return (
    <div className={`flex flex-col border-b border-gray-700 min-h-0 ${productsOpen ? 'flex-1' : 'flex-shrink-0'}`}>
      <SectionHeader
        icon={<IconBox />}
        label={label}
        isOpen={productsOpen}
        onToggle={onToggleOpen}
        required={selectedCount === 0}
        badge={selectedCount > 0 && (
          <span className="text-[10px] font-bold text-blue-300 bg-blue-500/20 rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
            {selectedCount}
          </span>
        )}
        onClear={selectedCount > 0 ? (e) => { e.stopPropagation(); onClear(); } : undefined}
      />

      {productsOpen && (
        <>
          <div className="px-3 pb-2 flex-shrink-0">
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}…`}
              className="w-full bg-white/8 border border-white/10 rounded-md px-2.5 py-1.5 text-label text-dark-text placeholder:text-dark-muted outline-none focus:border-blue-400 transition-colors duration-fast"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 scrollbar-touch">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-8 text-gray-600 text-sm">
                {search ? 'No products match your search' : 'No products imported yet'}
              </div>
            ) : (
                  <div className="p-2 space-y-1">
                {products.map(product => {
                  const selected = isProductSelected(product.productId);
                  return (
                    <button
                      type="button"
                      key={product.productId}
                      onClick={() => onToggleProduct(product)}
                      className={`w-full flex items-start gap-2 p-2.5 rounded-lg text-left transition-all duration-base touch-target ${
                        selected
                          ? 'bg-blue-500/15 border border-blue-400/40'
                          : 'bg-white/5 border border-transparent hover:bg-white/8 hover:border-white/15'
                      }`}
                    >
                      {product.primaryImage ? (
                        <img
                          src={product.primaryImage}
                          alt={product.name}
                          className="w-12 h-12 rounded object-cover flex-shrink-0 bg-gray-700"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <IconBox className="w-5 h-5 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-label font-medium text-dark-text truncate">{product.name}</div>
                        <div className="text-meta text-dark-muted truncate">{product.classification}</div>
                        {product.variant && (
                          <div className="text-meta text-dark-label truncate">{product.variant}</div>
                        )}
                      </div>
                      {selected && (
                        <div className="flex-shrink-0 mt-1">
                          <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};



// ─── Main component ────────────────────────────────────────────────────────────

const ContextPanel: React.FC<ContextPanelProps> = ({ accessToken, tenantId, selectedWorkflow }) => {
  const {
    context,
    addProduct,
    removeProduct,
    setSelectedProducts,
    isProductSelected,
    clearProducts,
    clearSheetSelections,
  } = useStudioContext();

  // Products data
  const [products, setProducts]           = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchQuery, setSearchQuery]     = useState('');

  // Panel / section state
  const [collapsed, setCollapsed]         = useState(false);
  const [productsOpen, setProductsOpen]   = useState(true);
  // Transient notice surfaced inside the panel — currently used to inform the
  // user that the cart was auto-trimmed when switching to a single-select
  // workflow. Cleared automatically after a short display window.
  const [trimNotice, setTrimNotice]       = useState<string | null>(null);

  // Derive workflow-declared context sections from the active manifest.
  // Sorted alphabetically by display title so layout is predictable.
  const contextInputs = useMemo<ManifestInput[]>(() => {
    const inputs = selectedWorkflow?.manifest.inputs || [];
    return inputs
      .filter(i => i.source === 'context')
      .slice()
      .sort((a, b) => {
        const la = (a.title || a.context_id || a.key).toLowerCase();
        const lb = (b.title || b.context_id || b.key).toLowerCase();
        return la.localeCompare(lb);
      });
  }, [selectedWorkflow]);

  const sheetRowInputs = contextInputs.filter(i => i.type === 'sheet-row');

  // ── Resolve the "Products" section input ───────────────────────────────────
  // Two paths can declare a need for the Products picker:
  // Products section is driven by the manifest: the compiler emits a
  // `product-list` context input whenever the workflow contains a
  // CCStudio_Product normaliser node, and omits it otherwise. Selection is
  // hard-capped at 1 (allow_multiple: false from the compiler).
  const productListInput = useMemo<ManifestInput | undefined>(
    () => contextInputs.find(i => i.type === 'product-list'),
    [contextInputs]
  );

  const allowMultiple = productListInput?.allow_multiple !== false;

  // Render list: context inputs sorted alphabetically by display label so
  // section order is predictable across workflows.
  const renderableInputs = useMemo<ManifestInput[]>(() => {
    return contextInputs.slice().sort((a, b) => {
      const la = (a.title || a.context_id || a.key).toLowerCase();
      const lb = (b.title || b.context_id || b.key).toLowerCase();
      return la.localeCompare(lb);
    });
  }, [contextInputs]);

  useEffect(() => {
    if (selectedWorkflow && renderableInputs.length === 0) {
      setCollapsed(true);
    }
  }, [selectedWorkflow, renderableInputs.length]);

  // ── Tokens ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    productService.setAccessToken(accessToken);
  }, [accessToken]);

  // ── Clear context when tenant changes ──────────────────────────────────────
  // All sidebar selections are tenant-scoped — wipe them whenever the active
  // tenantId changes so stale data from the previous tenant is never visible.
  useEffect(() => {
    clearProducts();
    clearSheetSelections();
    setProducts([]);
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Products ───────────────────────────────────────────────────────────────
  const loadProducts = useCallback(async () => {
    if (!productListInput) return; // Workflow doesn't use products — skip the fetch
    setLoadingProducts(true);
    try {
      const res = await productService.listProducts({
        tenantId,
        search: searchQuery || undefined,
      });
      setProducts(res.products);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoadingProducts(false);
    }
  }, [searchQuery, tenantId, productListInput]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Auto-trim to a single product whenever the active workflow is single-select
  // (covers both the initial load and a workflow switch from multi → single).
  // Picks the first currently-selected item; the rest are dropped silently
  // and a transient notice is surfaced so the change isn't invisible.
  useEffect(() => {
    if (!productListInput) return;
    if (allowMultiple) return;
    if (context.selectedProducts.length > 1) {
      const dropped = context.selectedProducts.length - 1;
      const kept = context.selectedProducts[0];
      setSelectedProducts([kept]);
      setTrimNotice(
        `Trimmed to 1 product (kept "${kept.name}", dropped ${dropped}) — this workflow allows only one.`
      );
    }
  }, [allowMultiple, productListInput, context.selectedProducts, setSelectedProducts]);

  // Auto-dismiss the trim notice after a short window so it doesn't linger.
  useEffect(() => {
    if (!trimNotice) return;
    const t = setTimeout(() => setTrimNotice(null), 4000);
    return () => clearTimeout(t);
  }, [trimNotice]);

  const handleProductToggle = (product: Product) => {
    if (isProductSelected(product.productId)) {
      removeProduct(product.productId);
      return;
    }
    if (allowMultiple) {
      addProduct(product);
    } else {
      // Radio behaviour: replace any prior selection with this one
      setSelectedProducts([product]);
    }
  };

  // ── Collapsed icon rail ─────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <div className="w-12 h-full max-lg:w-full bg-gray-900 border-r max-lg:border-r-0 max-lg:border-t border-gray-700 flex flex-col max-lg:flex-row items-center pt-3 max-lg:pt-0 max-lg:px-3 gap-5 max-lg:gap-4 overflow-x-auto scrollbar-touch transition-[width] duration-base">
        {/* Expand button */}
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="text-gray-600 hover:text-gray-300 p-1 transition-colors duration-fast touch-target flex items-center justify-center"
          title="Expand context panel"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Sheet selection indicators — one bullet per active sheet section */}
        {sheetRowInputs.map(input => {
          const sel = context.sheetSelections[input.context_id || input.key];
          const isActive = !!sel;
          return (
            <div
              key={input.key}
              className="relative"
              title={`${input.title || input.key}${sel ? `: ${sel.rowTitle}` : ''}`}
            >
              <IconSheet className={`w-5 h-5 ${isActive ? 'text-purple-400' : 'text-gray-600'}`} />
              {isActive && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-400 rounded-full border-2 border-gray-900" />
              )}
            </div>
          );
        })}

        {/* Products icon — only when the workflow declares a product-list section */}
        {productListInput && (
          <div
            className="relative"
            title={`Products${context.selectedProducts.length > 0 ? ` (${context.selectedProducts.length} selected)` : ''}`}
          >
            <IconBox className={`w-5 h-5 ${context.selectedProducts.length > 0 ? 'text-blue-400' : 'text-gray-600'}`} />
            {context.selectedProducts.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-gray-900">
                {context.selectedProducts.length}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Expanded panel ────────────────────────────────────────────────────────────
  return (
    <div className="w-72 max-lg:w-full h-full bg-gray-900 border-r max-lg:border-r-0 max-lg:border-t border-gray-700 flex flex-col text-gray-200 overflow-hidden relative">

      {/* ── Panel header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-700 flex-shrink-0">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Context</h3>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="text-gray-600 hover:text-gray-300 p-1 transition-colors duration-fast touch-target flex items-center justify-center"
          title="Collapse panel"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* ── Workflow-driven sections ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto min-h-0 scrollbar-touch">
        {!selectedWorkflow ? (
          <div className="px-3 py-6 text-center text-gray-500 text-xs">
            Select a workflow to see its context options.
          </div>
        ) : renderableInputs.length === 0 ? (
          <div className="px-3 py-6 text-center text-gray-500 text-xs">
            This workflow has no context sections.
          </div>
        ) : renderableInputs.map(input => {
          if (input.type === 'sheet-row') {
            return (
              <SheetRowSection
                key={input.key}
                input={input}
                accessToken={accessToken}
              />
            );
          }
          if (input.type === 'product-list') {
            return (
              <ProductsSection
                key={input.key}
                input={input}
                products={products}
                loading={loadingProducts}
                search={searchQuery}
                onSearchChange={setSearchQuery}
                productsOpen={productsOpen}
                onToggleOpen={() => setProductsOpen(o => !o)}
                isProductSelected={isProductSelected}
                onToggleProduct={handleProductToggle}
                onClear={clearProducts}
                selectedCount={context.selectedProducts.length}
              />
            );
          }
          return null;
        })}
      </div>

      {/* ── Auto-trim notice ──────────────────────────────────────────────── */}
      {trimNotice && (
        <div
          className="absolute bottom-3 left-3 right-3 bg-amber-500/15 border border-amber-400/30 text-amber-200 text-[11px] leading-snug rounded-md px-3 py-2 shadow-lg flex items-start gap-2"
          role="status"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1 min-w-0">{trimNotice}</span>
          <button
            type="button"
            onClick={() => setTrimNotice(null)}
            className="flex-shrink-0 text-amber-300 hover:text-amber-100 transition-colors touch-target flex items-center justify-center"
            title="Dismiss"
            aria-label="Dismiss notice"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ContextPanel;

